/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * MarkdownRenderer - Renders markdown content with syntax highlighting
 * 
 * Exact port of frontend/src/components/MarkdownRenderer.jsx
 * 
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists, etc.)
 * - Syntax highlighting for code blocks
 * - Theme-aware (dark/light mode support)
 */
import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from '@/components/markdown/MermaidDiagram';
import {
  PrismSyntaxHighlighter as SyntaxHighlighter,
  prismDarkStyle,
  prismLightStyle,
  resolveSyntaxLanguage,
} from '@/lib/prismHighlighter';
import { isMermaidLanguage, normalizeMermaidMarkdown } from '@/utils/mermaidMarkdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isDark?: boolean;
}

interface CodeBlockProps {
  node?: unknown;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const MarkdownRenderer = memo(({ content, className = '', isDark = false }: MarkdownRendererProps) => {
  const normalizedContent = useMemo(() => normalizeMermaidMarkdown(content), [content]);

  // Custom code block component with syntax highlighting
  const CodeBlock = ({ inline, className: codeClassName, children, ...props }: CodeBlockProps) => {
    const match = /language-([\w-]+)/.exec(codeClassName || '');
    const language = match ? match[1] : '';
    const resolvedLanguage = resolveSyntaxLanguage(language);
    const codeString = String(children).replace(/\n$/, '');
    
    // Handle inline code
    if (inline) {
      return (
        <code
          style={{
            backgroundColor: isDark ? '#374151' : '#f3f4f6',
            color: isDark ? '#f9fafb' : '#1f2937',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.875em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    if (isMermaidLanguage(language)) {
      return <MermaidDiagram chart={codeString} isDark={isDark} />;
    }
    
    // Handle code blocks with syntax highlighting
    return (
      <div style={{ position: 'relative', margin: '8px 0' }}>
        {language && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '11px',
              color: isDark ? '#9ca3af' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {language}
          </div>
        )}
        <SyntaxHighlighter
          style={isDark ? prismDarkStyle : prismLightStyle}
          language={resolvedLanguage}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '8px',
            fontSize: '13px',
            padding: '16px',
          }}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  };
  
  // Custom components for markdown elements
  const components = {
    code: CodeBlock,
    // Style tables
    table: ({ children }: { children?: React.ReactNode }) => (
      <div style={{ overflowX: 'auto', margin: '8px 0' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '14px',
          }}
        >
          {children}
        </table>
      </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
          backgroundColor: isDark ? '#374151' : '#f3f4f6',
          textAlign: 'left',
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
        }}
      >
        {children}
      </td>
    ),
    // Style blockquotes
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote
        style={{
          borderLeft: `4px solid ${isDark ? '#6b7280' : '#d1d5db'}`,
          margin: '8px 0',
          paddingLeft: '16px',
          color: isDark ? '#9ca3af' : '#6b7280',
          fontStyle: 'italic',
        }}
      >
        {children}
      </blockquote>
    ),
    // Style links
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: isDark ? '#60a5fa' : '#2563eb',
          textDecoration: 'underline',
        }}
      >
        {children}
      </a>
    ),
    // Style lists
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li style={{ margin: '4px 0' }}>{children}</li>
    ),
    // Style headings
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 style={{ fontSize: '1.5em', fontWeight: 700, margin: '16px 0 8px' }}>{children}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 style={{ fontSize: '1.3em', fontWeight: 600, margin: '14px 0 6px' }}>{children}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 style={{ fontSize: '1.1em', fontWeight: 600, margin: '12px 0 4px' }}>{children}</h3>
    ),
    // Style paragraphs
    p: ({ children }: { children?: React.ReactNode }) => (
      <p style={{ margin: '8px 0', lineHeight: 1.6 }}>{children}</p>
    ),
    // Style horizontal rules
    hr: () => (
      <hr
        style={{
          border: 'none',
          borderTop: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          margin: '16px 0',
        }}
      />
    ),
  };
  
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components as any}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
